import { useEffect, useState } from 'react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { downloadTextFile } from '@ludolio/shared-labs/format';
import type { LabQuizResponsePage, LabQuizView } from '@/lib/types';

export function QuizResponses({
  quizId,
  versions,
  currentVersionId,
}: {
  quizId: string;
  versions: LabQuizView['versions'];
  currentVersionId: string;
}) {
  const [versionId, setVersionId] = useState(currentVersionId);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<LabQuizResponsePage | null>(null);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState<'csv' | 'json' | null>(null);

  useEffect(() => {
    setVersionId(currentVersionId);
    setPage(1);
  }, [currentVersionId, quizId]);

  useEffect(() => {
    let active = true;
    setData(null);
    labsApi.quizResponses(quizId, page, versionId)
      .then((result) => {
        if (!active) return;
        setData(result);
        setError('');
      })
      .catch((err) => {
        if (active) setError(getErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [page, quizId, versionId]);

  const exportFile = async (format: 'csv' | 'json') => {
    setExporting(format);
    try {
      const file = await labsApi.exportQuiz(quizId, format, versionId);
      downloadTextFile(file.filename, file.contentType, file.body);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setExporting(null);
    }
  };

  if (error) return <p className="text-sm text-destructive" role="alert">{error}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Loading responses…</p>;

  const summary = data.summary;
  const pageCount = Math.max(1, Math.ceil(data.responses.total / data.responses.pageSize));

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold">
            Responses{summary ? ` · ${summary.responseCount}` : ''}
          </h2>
          {versions.length > 1 && (
            <Select value={versionId} onValueChange={(value) => { setVersionId(value); setPage(1); }}>
              <SelectTrigger aria-label="Response version" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {versions.map((version) => (
                  <SelectItem key={version.id} value={version.id}>
                    Version {version.version}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {data.canExport && (
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!summary || exporting !== null} onClick={() => void exportFile('csv')}>
              {exporting === 'csv' ? 'Preparing…' : 'Download CSV'}
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={!summary || exporting !== null} onClick={() => void exportFile('json')}>
              {exporting === 'json' ? 'Preparing…' : 'Download JSON'}
            </Button>
          </div>
        )}
      </div>

      {!summary && <p className="text-sm text-muted-foreground">Open the quiz before responses can be collected.</p>}
      {summary && summary.responseCount === 0 && <p className="text-sm text-muted-foreground">No responses yet.</p>}
      {summary && summary.responseCount > 0 && (
        <ul className="space-y-4">
          {summary.questions.filter((question) => question.options).map((question) => (
            <li key={question.id} className="space-y-2">
              <p className="text-sm font-medium">{question.prompt}</p>
              <ul className="space-y-1">
                {question.options?.map((option) => (
                  <li key={option.id} className="grid grid-cols-[minmax(0,1fr)_3rem] gap-4 text-sm">
                    <span className="truncate">{option.label}</span>
                    <span className="text-right text-muted-foreground">{option.count}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
          {summary.complete === false && (
            <p className="text-sm text-muted-foreground">Download the file to see every response.</p>
          )}
        </ul>
      )}

      {data.responses.items.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {data.responses.items.map((response) => (
            <li key={response.id} className="space-y-2 px-4 py-3 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <span className="font-medium">{response.respondent.name} · {response.respondent.email}</span>
                <span className="text-muted-foreground">{formatDate(response.submittedAt)}</span>
              </div>
              {response.answers.map((answer) => (
                <p key={answer.questionId} className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-4">
                  <span className="text-muted-foreground">{answer.prompt}</span>
                  <span className="min-w-0 whitespace-pre-line">{answer.answer || '—'}</span>
                </p>
              ))}
            </li>
          ))}
        </ul>
      )}

      {pageCount > 1 && (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
            Previous
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>
            Next
          </Button>
        </div>
      )}
    </section>
  );
}
